import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutDashboard,
  TrendingUp,
  FileText,
  Anchor,
  Package,
  DollarSign,
  Brain,
  ChevronDown,
  ChevronLeft,
  ShoppingCart,
  ShoppingBag,
  Truck,
  BookOpen,
  Users,
  BarChart3,
  Calculator,
  Settings,
  Sparkles,
  Ship,
  Grid3X3,
  Code2,
  MessageSquareMore,
  Bot,
  FileQuestion,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  ExternalLink,
  Bell,
  Building2,
  Tag,
  Receipt,
  CreditCard,
  BookMarked,
  Wallet,
  PieChart,
  MapPin,
  Map,
  ScrollText,
  Globe,
  Download,
  Compass,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

// ─── Navigation style tokens ──────────────────────────────────────────────────
const NAV_STYLES = {
  activeMain:
    "bg-gradient-to-r from-blue-600/25 via-blue-500/15 to-teal-500/10 text-white font-medium shadow-[inset_0_0_0_1px_rgba(59,130,246,0.35)]",
  activeSub:
    "bg-white/[0.08] text-white font-medium shadow-[inset_3px_0_0_0_rgba(59,130,246,0.9)]",
  hover:
    "text-[#9CA3AF] hover:bg-white/[0.06] hover:text-white active:scale-[0.98]",
  dot: "before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-[3px] before:rounded-r-full before:bg-gradient-to-b before:from-blue-500 before:via-blue-400 before:to-teal-400 before:shadow-[0_0_8px_rgba(59,130,246,0.5)]",
};

const normalizePathname = (pathname = "") => {
  const p = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "");
  return p || "/";
};

/** Longest-prefix match so /booking/cancel highlights cancel, not booking. */
const resolveActiveNavId = (pathname, navIds = []) => {
  const path = normalizePathname(pathname);
  let bestId = null;
  let bestLen = 0;

  navIds.forEach((id) => {
    if (!id || id.includes("://")) return;
    const target = `/${id}`;
    const matches = path === target || path.startsWith(`${target}/`);
    if (matches && target.length > bestLen) {
      bestLen = target.length;
      bestId = id;
    }
  });

  return bestId;
};

const isNavIdActive = (pathname, itemId, activeNavId) =>
  Boolean(itemId && activeNavId === itemId);

const openInNewTab = (url, { external = false } = {}) => {
  const href = external
    ? url
    : `${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`;
  window.open(href, "_blank", "noopener,noreferrer");
};

const useNavPressFeedback = () => {
  const [pressingId, setPressingId] = useState(null);
  const onPressStart = (id) => setPressingId(id);
  const onPressEnd = () => setPressingId(null);
  const isPressing = (id) => pressingId === id;
  return { onPressStart, onPressEnd, isPressing };
};

// ─── Open in new tab (hover affordance) ───────────────────────────────────────
const OpenInNewTabButton = ({ href, external, label, className = "" }) => (
  <button
    type="button"
    title={`Open ${label} in new tab`}
    aria-label={`Open ${label} in new tab`}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      openInNewTab(href, { external });
    }}
    className={`flex-shrink-0 p-1 rounded-md opacity-0 pointer-events-none
      group-hover:opacity-100 group-hover:pointer-events-auto
      text-slate-400 hover:text-white hover:bg-white/10
      transition-all duration-200 ${className}`}
  >
    <ExternalLink className="w-3 h-3" strokeWidth={2} />
  </button>
);

// ─── NavItem (main level) ─────────────────────────────────────────────────────
const navLinkBase =
  "relative w-full flex items-center no-underline rounded-lg transition-all duration-200 ease-out";

const NavItem = ({
  id,
  label,
  icon: Icon,
  activeNavId,
  pathname,
  externalLink,
  isCollapsed = false,
  onNavigate,
  pressFeedback,
}) => {
  const isActive = isNavIdActive(pathname, id, activeNavId);
  const pressing = pressFeedback?.isPressing(id);

  const linkClassName = `${navLinkBase} ${isCollapsed ? "justify-center px-2" : "gap-3 px-3"
    } py-2.5 flex-1 min-w-0 ${pressing ? "scale-[0.98] opacity-90" : ""} ${isActive
      ? `${NAV_STYLES.activeMain} ${NAV_STYLES.dot}`
      : NAV_STYLES.hover
    }`;

  const handleExternalClick = (e) => {
    e.preventDefault();
    pressFeedback?.onPressStart(id);
    onNavigate?.();
    openInNewTab(externalLink, { external: true });
    pressFeedback?.onPressEnd();
  };

  const handleInternalClick = () => {
    pressFeedback?.onPressStart(id);
    onNavigate?.();
    pressFeedback?.onPressEnd();
  };

  const linkContent = (
    <>
      <Icon
        className={`w-[18px] h-[18px] flex-shrink-0 transition-colors duration-200 ${isActive ? "text-blue-300" : ""
          }`}
        strokeWidth={isActive ? 2.2 : 1.8}
      />
      {!isCollapsed && (
        <span className="text-sm text-left leading-none flex-1 truncate">
          {label}
        </span>
      )}
      {!isCollapsed && externalLink && (
        <ExternalLink
          className="w-3 h-3 ml-auto opacity-40 flex-shrink-0"
          strokeWidth={2}
        />
      )}
    </>
  );

  const navTo = id === "admin" ? "/admin/" : `/${id}`;

  return (
    <div
      className={`relative group flex items-center ${isCollapsed ? "" : "pr-1"}`}
    >
      {externalLink ? (
        <a
          href={externalLink}
          rel="noopener noreferrer"
          data-testid={`nav-${id}`}
          className={linkClassName}
          onClick={handleExternalClick}
        >
          {linkContent}
        </a>
      ) : (
        <Link
          to={navTo}
          data-testid={`nav-${id}`}
          className={linkClassName}
          onClick={handleInternalClick}
        >
          {linkContent}
        </Link>
      )}
      {!isCollapsed && !externalLink && (
        <OpenInNewTabButton href={navTo} label={label} />
      )}
      {isCollapsed && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
          {label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
        </div>
      )}
    </div>
  );
};

// ─── ParentNavBtn (top-level parent) ─────────────────────────────────────────
const ParentNavBtn = ({
  icon: Icon,
  label,
  isActive,
  isOpen,
  isCollapsed,
  tooltip,
  onClick,
  testId,
}) => (
  <div className="relative group">
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative w-full flex items-center ${isCollapsed ? "justify-center px-2" : "justify-between px-3"
        } py-2.5 rounded-lg transition-all duration-150 ${isActive
          ? `${NAV_STYLES.activeMain} ${NAV_STYLES.dot}`
          : NAV_STYLES.hover
        }`}
    >
      <div className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}>
        <Icon
          className="w-[18px] h-[18px] flex-shrink-0"
          strokeWidth={isActive ? 2.2 : 1.8}
        />
        {!isCollapsed && (
          <span className="font-medium text-sm">{label}</span>
        )}
      </div>
      {!isCollapsed && (
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 opacity-60 ${isOpen ? "rotate-0" : "-rotate-90"
            }`}
          strokeWidth={2.5}
        />
      )}
    </button>
    {isCollapsed && (
      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
        {tooltip || label}
        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
      </div>
    )}
  </div>
);

// ─── TreeMenu (outer vertical line) ──────────────────────────────────────────
const TreeMenu = ({ children, showLine = true }) => (
  <div className="relative mt-1 mb-1">
    {showLine && (
      <span
        aria-hidden
        className="absolute top-0 bottom-1 left-[25px] w-px bg-white/40 z-10 pointer-events-none"
      />
    )}
    {children}
  </div>
);

// ─── TreeItem (submenu leaf) ─────────────────────────────────────────────────
const TreeItem = ({
  id,
  label,
  activeNavId,
  pathname,
  onNavigate,
  testId,
  indent = "pl-9",
  pressFeedback,
}) => {
  const isActive = isNavIdActive(pathname, id, activeNavId);
  const pressing = pressFeedback?.isPressing(id);

  const navTo = `/${id}`;

  return (
    <div className="relative group flex items-center pr-1">
      <Link
        to={navTo}
        onClick={() => {
          pressFeedback?.onPressStart(id);
          onNavigate?.();
          pressFeedback?.onPressEnd();
        }}
        data-testid={testId || `nav-${id.replace("/", "-")}`}
        className={`relative flex flex-1 min-w-0 items-center gap-2 no-underline ${indent} pr-2 py-2 rounded-lg transition-all duration-200 text-sm ${pressing ? "scale-[0.99] opacity-90" : ""
          } ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hover}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-200 ${isActive
            ? "bg-blue-400 shadow-[0_0_6px_rgba(96,165,250,0.8)]"
            : "bg-white/25"
            }`}
          aria-hidden
        />
        <span className="flex-1 truncate">{label}</span>
      </Link>
      <OpenInNewTabButton href={navTo} label={label} />
    </div>
  );
};

// ─── TreeParent (submenu parent, e.g. Export/Import) ─────────────────────────
const TreeParent = ({
  icon: Icon,
  label,
  isActive,
  isOpen,
  onClick,
  children,
  testId,
}) => (
  <div className="relative">
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative w-full flex items-center justify-between pl-9 pr-3 py-2 mb-2 rounded-lg transition-all duration-150 text-sm ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hover
        }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-xs flex-shrink-0 ${isActive ? "text-blue-400" : "text-white/20"
            }`}
        >
          —
        </span>
        <span className="font-medium">{label}</span>
      </div>
      <ChevronDown
        className={`w-3 h-3 opacity-60 transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"
          }`}
        strokeWidth={2.5}
      />
    </button>

    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="overflow-hidden"
        >
          <div className="relative">
            <span
              aria-hidden
              className="absolute top-0 bottom-1 left-[48px] w-px bg-white/40 z-10 pointer-events-none"
            />
            <div className="pl-4">{children}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ─── NavContent ───────────────────────────────────────────────────────────────
const ALL_NAV_IDS = [
  "dashboard", "forwarder-dashboard", "customs", "containers", "financials", "email-summary",
  "quotation-report", "booking-report", "accounting-report",
  "maintain-vessel", "Commodity", "CountryClause", "Clause", "ChargesCode",
  "port-place", "maintain-locations", "Users",
  "accounting/chart-of-accounts", "accounting/charge-codes", "accounting/invoices",
  "accounting/bills", "accounting/journals", "accounting/payments", "accounting/reports",
  "rate-quote", "booking-request-list", "booking", "booking/cancel", "ocean/export/reports",
  "ocean/export/reports/booking", "ocean/export/reports/booking-salesperson",
  "import-rate-quote", "import-booking", "ocean/import/reports",
  "ocean/import/reports/booking", "ocean/import/reports/active-shipment", "ocean/import/reports/demurrage",
  "ocean/import/reports/trucking", "ocean/import/reports/status-audit", "ocean/import/reports/container-tracking",
  "ocean/import/reports/arrival-notice", "ocean/import/reports/eta-status",
  "ocean/import/reports/file-cancellation-report", "ocean/import/reports/arrival-notice-pre-alert-report",
  "rate-intelligence", "ocean-control-tower", "notifications", "rfq",
  "purchase-orders", "sales-orders", "load-plan", "mailbox",
  "ai-agent-setup", "ai-agent-dashboard", "ai-agent-reports",
  "vision-board", "address-book", "admin",
];

const NavContent = ({ isCollapsed, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const pressFeedback = useNavPressFeedback();

  const pathname = location.pathname;
  const activeNavId = useMemo(
    () => resolveActiveNavId(pathname, ALL_NAV_IDS),
    [pathname],
  );

  // ── Multiple menus can be open simultaneously ────────────────────────────────
  // Menus only collapse when a leaf item is selected, not when another is expanded.
  const [openMenus, setOpenMenus] = useState(new Set());

  // Ocean sub-menus are nested inside Ocean so they stay independent
  const [isOceanExportOpen, setIsOceanExportOpen] = useState(false);
  const [isOceanImportOpen, setIsOceanImportOpen] = useState(false);

  /** Toggle a top-level menu open/closed without affecting any other menus. */
  const toggleMenu = (name) =>
    setOpenMenus((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  /**
   * Called when a leaf nav item is selected.
   * Collapses every menu except the one the selected item belongs to,
   * then fires the parent onNavigate (closes mobile drawer, etc.).
   */
  const closeOtherMenus = (keep) =>
    setOpenMenus(new Set(keep ? [keep] : []));

  // Derive per-menu booleans from the Set
  const isAIInsightsOpen = openMenus.has("aiInsights");
  const isOceanOpen = openMenus.has("ocean");
  const isAIAgentOpen = openMenus.has("aiAgent");
  const isAccountingOpen = openMenus.has("accounting");
  const isReportsOpen = openMenus.has("reports");
  const isSystemMaintOpen = openMenus.has("systemMaint");

  // ── Menu item definitions ────────────────────────────────────────────────────
  const subMenuItems = [
    { id: "dashboard", label: t("aiInsights") },
    { id: "forwarder-dashboard", label: "Forwarder Dashboard" },
    { id: "customs", label: t("customsFiling") },
    { id: "containers", label: t("containerTracking") },
    { id: "financials", label: t("financials") },
    { id: "email-summary", label: t("emailSummary") },
  ];

  const reportMenuItems = [
    { id: "quotation-report", label: t("quotationReport") },
    { id: "booking-report", label: t("bookingReport") },
    { id: "accounting-report", label: t("accountingReport") },
  ];

  const systemMaintItems = [
    { id: "maintain-vessel", label: t("vessel") },
    { id: "Commodity", label: t("commodity") },
    { id: "CountryClause", label: t("countryClause") },
    { id: "Clause", label: t("clauses") },
    { id: "ChargesCode", label: t("chargesCodeMaint") },
    { id: "port-place", label: t("Ports / Places") },
    { id: "maintain-locations", label: t("Locations") },
    { id: "Users", label: t("Users") },
  ];

  const accountingMenuItems = [
    { id: "accounting/chart-of-accounts", label: t("chartOfAccounts") },
    { id: "accounting/charge-codes", label: t("chargeCodes") },
    { id: "accounting/invoices", label: t("customerInvoices") },
    { id: "accounting/bills", label: t("vendorBills") },
    { id: "accounting/journals", label: t("journalEntries") },
    { id: "accounting/payments", label: t("payments") },
    { id: "accounting/reports", label: t("reports") },
  ];

  const oceanExportItems = [
    { id: "rate-quote", label: t("rateQuoteList") },
    { id: "booking-request-list", label: t("Booking Request") },
    { id: "booking", label: t("shipmentList") },
    { id: "booking/cancel", label: t("cancelShipment") },
    { id: "ocean/export/reports", label: t("reports") },
  ];

  const oceanImportItems = [
    { id: "import-rate-quote", label: t("importRateQuote") },
    { id: "import-booking", label: t("importBooking") },
    { id: "ocean/import/reports", label: t("reports") },
  ];

  // ── Active-section flags (used for styling + auto-open) ────────────────────
  const isOceanActive =
    oceanExportItems.some((i) => isNavIdActive(pathname, i.id, activeNavId)) ||
    oceanImportItems.some((i) => isNavIdActive(pathname, i.id, activeNavId));

  const isAccountingActive = accountingMenuItems.some((i) =>
    isNavIdActive(pathname, i.id, activeNavId),
  );
  const isReportsActive = reportMenuItems.some((i) =>
    isNavIdActive(pathname, i.id, activeNavId),
  );
  const isSystemMaintActive = systemMaintItems.some((i) =>
    isNavIdActive(pathname, i.id, activeNavId),
  );
  const isAIInsightsSectionActive = subMenuItems.some((i) =>
    isNavIdActive(pathname, i.id, activeNavId),
  );
  const isAIAgentActive = ["ai-agent-setup", "ai-agent-dashboard", "ai-agent-reports"].some(
    (id) => isNavIdActive(pathname, id, activeNavId),
  );

  const isAnyOceanSubmenuOpen = isOceanExportOpen || isOceanImportOpen;

  // ── Auto-open the right menu when the active route changes ──────────────────
  // Uses additive set updates so other open menus are not disturbed on route change.
  useEffect(() => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      if (isOceanActive) next.add("ocean");
      if (isAccountingActive) next.add("accounting");
      if (isReportsActive) next.add("reports");
      if (isSystemMaintActive) next.add("systemMaint");
      if (isAIAgentActive) next.add("aiAgent");
      if (isAIInsightsSectionActive) next.add("aiInsights");
      return next;
    });

    // Ocean sub-menus auto-open independently
    if (oceanExportItems.some((i) => isNavIdActive(pathname, i.id, activeNavId)))
      setIsOceanExportOpen(true);
    if (oceanImportItems.some((i) => isNavIdActive(pathname, i.id, activeNavId)))
      setIsOceanImportOpen(true);
  }, [
    pathname,
    activeNavId,
    isOceanActive,
    isAccountingActive,
    isReportsActive,
    isSystemMaintActive,
    isAIAgentActive,
    isAIInsightsSectionActive,
  ]);

  // ── Shared motion props for all expandable sections ─────────────────────────
  const accordionMotion = {
    initial: { height: 0, opacity: 0 },
    animate: { height: "auto", opacity: 1 },
    exit: { height: 0, opacity: 0 },
    transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
    className: "overflow-hidden",
  };

  /**
   * Returns an onNavigate callback scoped to a menu section.
   * When called it:
   *   1. Collapses all menus except `menuName` (the section the item belongs to)
   *   2. Fires the outer onNavigate (e.g. closes the mobile drawer)
   */
  const navFrom = (menuName) => () => {
    closeOtherMenus(menuName);
    onNavigate?.();
  };

  // Nested tree item that knows which section it lives in
  const NestedTreeItem = ({ id, label, testId, sectionNav }) => (
    <TreeItem
      id={id}
      label={label}
      activeNavId={activeNavId}
      pathname={pathname}
      onNavigate={sectionNav}
      testId={testId}
      indent="pl-12"
      pressFeedback={pressFeedback}
    />
  );

  return (
    <>
      {/* ── Dashboard (default home) ── */}
      {/* <div className="relative group mb-1">
        <button
          onClick={() => {
            navigate("/forwarder-dashboard");
            onNavigate();
          }}
          data-testid="nav-forwarder-dashboard"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${(pathname === "/forwarder-dashboard" || pathname.startsWith("/forwarder-dashboard/"))
            ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-md"
            : ""
            }`}
        >
          <LayoutDashboard
            className="w-[18px] h-[18px] flex-shrink-0"
            strokeWidth={2}
          />
          {!isCollapsed && (
            <span className="font-semibold text-sm">Dashboard</span>
          )}
        </button>
        {isCollapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
            Dashboard
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800 dark:border-r-slate-700" />
          </div>
        )}
      </div> */}

      {/* ── AI Insights ── */}
      <div className="relative group mb-2">
        <button
          onClick={() => {
            if (isCollapsed) { navigate("/dashboard"); onNavigate(); }
            else toggleMenu("aiInsights");
          }}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg hover:brightness-110 ${isAIInsightsSectionActive
            ? "bg-gradient-to-r from-blue-600/25 via-blue-500/15 to-teal-500/10"
            : ""
            }`}
          data-testid="nav-ai-insights"
        >
          <div className="flex items-center gap-3">
            <Brain className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
            {!isCollapsed && (
              <span className="font-semibold text-sm">{t("aiInsights")}</span>
            )}
          </div>
          {!isCollapsed && (
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${isAIInsightsOpen ? "rotate-0" : "-rotate-90"
                }`}
              strokeWidth={2.5}
            />
          )}
        </button>

        {isCollapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
            {t("aiInsights")}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
          </div>
        )}

        <AnimatePresence>
          {isAIInsightsOpen && !isCollapsed && (
            <motion.div {...accordionMotion}>
              <TreeMenu showLine>
                {subMenuItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeNavId={activeNavId}
                    pathname={pathname}
                    onNavigate={navFrom("aiInsights")}
                    pressFeedback={pressFeedback}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Main nav items ── */}
      <div className="space-y-1 pt-1">
        <NavItem
          id="ratematrix-cargorates"
          label="RateMatrix"
          icon={BarChart3}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
          externalLink="https://app.cargorates.ai/WebPortal/SignIn.aspx?b=Uzcvc3R0MXhaR3FCcWxVZlFqekV1dz09&d=YWl3dkJKckh1SVR1dnEzUDlSa1ZCZz09"
        />
        <NavItem
          id="rate-intelligence"
          label="Rate Intelligence"
          icon={Sparkles}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="ocean-control-tower"
          label={t("controlTower")}
          icon={Compass}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="notifications"
          label={t("notificationCenter")}
          icon={Bell}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />

        {/* ── Ocean ── */}
        <ParentNavBtn
          icon={Ship}
          label={t("ocean")}
          isActive={isOceanActive}
          isOpen={isOceanOpen}
          isCollapsed={isCollapsed}
          testId="nav-ocean"
          onClick={() => {
            if (isCollapsed) { navigate("/rate-quote"); onNavigate(); }
            else toggleMenu("ocean");
          }}
        />
        <AnimatePresence>
          {isOceanOpen && !isCollapsed && (
            <motion.div {...accordionMotion}>
              <TreeMenu showLine={!isAnyOceanSubmenuOpen}>
                <TreeParent
                  label={t("export")}
                  isActive={oceanExportItems.some((i) =>
                    isNavIdActive(pathname, i.id, activeNavId),
                  )}
                  isOpen={isOceanExportOpen}
                  testId="nav-ocean-export"
                  onClick={() => setIsOceanExportOpen((v) => !v)}
                >
                  {oceanExportItems.map((item) => (
                    <NestedTreeItem
                      key={item.id}
                      {...item}
                      sectionNav={navFrom("ocean")}
                    />
                  ))}
                </TreeParent>

                <TreeParent
                  label={t("import")}
                  isActive={oceanImportItems.some((i) =>
                    isNavIdActive(pathname, i.id, activeNavId),
                  )}
                  isOpen={isOceanImportOpen}
                  testId="nav-ocean-import"
                  onClick={() => setIsOceanImportOpen((v) => !v)}
                >
                  {oceanImportItems.map((item) => (
                    <NestedTreeItem
                      key={item.id}
                      {...item}
                      sectionNav={navFrom("ocean")}
                    />
                  ))}
                </TreeParent>
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        <NavItem
          id="rfq"
          label={t("rfq")}
          icon={FileQuestion}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="quote-followup"
          label={t("quoteFollowup")}
          icon={MessageSquareMore}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
          externalLink="https://app.mylogpal.ai/SignIn.aspx?b=aXFoYXRYNGJNMUpwdkxkY2xreDZ1dz09&d=dy9XU2RjVGdqNDVxZDFNSmlIbURjQT09"
        />
        <NavItem
          id="purchase-orders"
          label={t("purchaseOrder")}
          icon={ShoppingCart}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="sales-orders"
          label={t("salesOrder")}
          icon={ShoppingBag}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="load-plan"
          label={t("loadPlan")}
          icon={Truck}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="mailbox"
          label={t("aiAutomation")}
          icon={Sparkles}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />

        {/* ── AI Agent ── */}
        <ParentNavBtn
          icon={Bot}
          label={t("aiAgent")}
          isActive={isAIAgentActive}
          isOpen={isAIAgentOpen}
          isCollapsed={isCollapsed}
          testId="nav-ai-agent"
          onClick={() => {
            if (isCollapsed) { navigate("/ai-agent-setup"); onNavigate(); }
            else toggleMenu("aiAgent");
          }}
        />
        <AnimatePresence>
          {isAIAgentOpen && !isCollapsed && (
            <motion.div {...accordionMotion}>
              <TreeMenu showLine>
                <TreeItem
                  id="ai-agent-setup"
                  label={t("agentSetup")}
                  activeNavId={activeNavId}
                  pathname={pathname}
                  onNavigate={navFrom("aiAgent")}
                  pressFeedback={pressFeedback}
                />
                <TreeItem
                  id="ai-agent-dashboard"
                  label={t("dashboard")}
                  activeNavId={activeNavId}
                  pathname={pathname}
                  onNavigate={navFrom("aiAgent")}
                  pressFeedback={pressFeedback}
                />
                <TreeItem
                  id="ai-agent-reports"
                  label={t("reports")}
                  activeNavId={activeNavId}
                  pathname={pathname}
                  onNavigate={navFrom("aiAgent")}
                  pressFeedback={pressFeedback}
                />
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        <NavItem
          id="api-docs"
          label={t("api")}
          icon={Code2}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
          externalLink="https://api.shipez.ai/swagger/index.html"
        />
        <NavItem
          id="vision-board"
          label={t("visionBoard")}
          icon={Grid3X3}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="address-book"
          label={t("addressBook")}
          icon={Users}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />
        <NavItem
          id="admin"
          label="Admin Panel"
          icon={Building2}
          activeNavId={activeNavId}
          pathname={pathname}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
        />

        {/* ── Accounting ── */}
        <ParentNavBtn
          icon={Calculator}
          label={t("accounting")}
          isActive={isAccountingActive}
          isOpen={isAccountingOpen}
          isCollapsed={isCollapsed}
          testId="nav-accounting"
          onClick={() => {
            if (isCollapsed) { navigate("/accounting/chart-of-accounts"); onNavigate(); }
            else toggleMenu("accounting");
          }}
        />
        <AnimatePresence>
          {isAccountingOpen && !isCollapsed && (
            <motion.div {...accordionMotion}>
              <TreeMenu showLine>
                {accountingMenuItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeNavId={activeNavId}
                    pathname={pathname}
                    onNavigate={navFrom("accounting")}
                    pressFeedback={pressFeedback}
                    testId={`nav-${item.id.replace("/", "-")}`}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Reports ── */}
        <ParentNavBtn
          icon={BarChart3}
          label={t("reportsMenu")}
          isActive={isReportsActive}
          isOpen={isReportsOpen}
          isCollapsed={isCollapsed}
          onClick={() => {
            if (isCollapsed) { navigate("/quotation-report"); onNavigate(); }
            else toggleMenu("reports");
          }}
        />
        <AnimatePresence>
          {isReportsOpen && !isCollapsed && (
            <motion.div {...accordionMotion}>
              <TreeMenu showLine>
                {reportMenuItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeNavId={activeNavId}
                    pathname={pathname}
                    onNavigate={navFrom("reports")}
                    pressFeedback={pressFeedback}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── System Maintenance ── */}
        <ParentNavBtn
          icon={Settings}
          label={t("systemMaintenance")}
          isActive={isSystemMaintActive}
          isOpen={isSystemMaintOpen}
          isCollapsed={isCollapsed}
          onClick={() => {
            if (isCollapsed) { navigate("/maintain-vessel"); onNavigate(); }
            else toggleMenu("systemMaint");
          }}
        />
        <AnimatePresence>
          {isSystemMaintOpen && !isCollapsed && (
            <motion.div {...accordionMotion}>
              <TreeMenu showLine>
                {systemMaintItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeNavId={activeNavId}
                    pathname={pathname}
                    onNavigate={navFrom("systemMaint")}
                    pressFeedback={pressFeedback}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ isOpen, onClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const masterCompanyDetailsRedux = useSelector(
    (state) => state.common?.MasterCompanyDetails,
  );

  const MasterCompanyDetail = useMemo(() => {
    const row = masterCompanyDetailsRedux?.[0];
    if (row && Object.keys(row).length > 0) return row;
    try {
      const parsed = JSON.parse(
        localStorage.getItem("MasterCompanyDetails") ?? "[]",
      );
      return parsed?.[0] ?? {};
    } catch {
      return {};
    }
  }, [masterCompanyDetailsRedux]);

  const desktopLogoSrc =
    MasterCompanyDetail?.CompanyLogo ||
    MasterCompanyDetail?.CompanyLogoDark ||
    "";
  const mobileLogoSrc =
    MasterCompanyDetail?.CompanyLogoMobile ||
    MasterCompanyDetail?.CompanyLogo ||
    "";

  const isMobileView =
    typeof window !== "undefined" && window.innerWidth < 1024;
  const effectiveCollapsed = isMobileView ? false : isCollapsed;

  const handleNavigate = useCallback(() => {
    if (onClose && window.innerWidth < 1024) onClose();
  }, [onClose]);

  const sidebarBase =
    "bg-gradient-to-b from-[#0A0A0A] to-[#121212] border-r border-slate-800 flex flex-col text-slate-300";

  const DesktopFooter = () => (
    <div className="p-3 space-y-2">
      <button
        onClick={() => setIsCollapsed((v) => !v)}
        className="w-full flex items-center justify-center gap-2 h-9 rounded-lg
                   text-[#9CA3AF] hover:text-white hover:bg-white/5
                   border border-white/5 transition-all duration-200"
        title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <ChevronLeft
          className={`w-4 h-4 transition-transform duration-200 ${effectiveCollapsed ? "rotate-180" : ""
            }`}
          strokeWidth={2}
        />
        {!effectiveCollapsed && (
          <span className="text-[12px] font-medium">Collapse</span>
        )}
      </button>
    </div>
  );

  const MobileFooter = () => (
    <div className="p-3 border-t border-slate-800">
      <div className="bg-slate-800/80 rounded-lg px-2 py-2 flex items-center justify-center border border-white/5">
        <span className="font-semibold text-lime-400 text-xs">v1.22.25</span>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1, width: effectiveCollapsed ? 72 : 256 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className={`hidden lg:flex ${sidebarBase} relative flex-shrink-0 h-screen sticky top-0`}
        style={{ width: effectiveCollapsed ? 72 : 256 }}
        data-testid="sidebar"
      >
        <div className="p-4 border-b border-slate-800 flex items-center justify-center min-h-[64px]">
          <img
            src={desktopLogoSrc}
            alt={MasterCompanyDetail?.CompanyName || "Company"}
            width={effectiveCollapsed ? 32 : 160}
            height={effectiveCollapsed ? 32 : 48}
            decoding="async"
            fetchPriority="high"
            loading="eager"
            className={
              effectiveCollapsed
                ? "h-8 w-8 object-contain rounded"
                : "h-12 w-auto max-w-[160px] object-contain"
            }
          />
        </div>

        <nav
          className={`flex-1 min-h-0 ${effectiveCollapsed ? "p-2" : "p-3"
            } space-y-0.5 overflow-y-auto overflow-x-hidden`}
          data-testid="sidebar-nav"
        >
          <NavContent
            isCollapsed={effectiveCollapsed}
            onNavigate={handleNavigate}
          />
        </nav>

        <DesktopFooter />
      </motion.aside>

      {/* ── Mobile Sidebar (drawer) ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={`fixed inset-y-0 left-0 z-50 w-[280px] sm:w-[300px] ${sidebarBase} lg:hidden shadow-2xl`}
            data-testid="mobile-sidebar"
          >
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <img
                src={mobileLogoSrc}
                alt={MasterCompanyDetail?.CompanyName || "Company"}
                className="h-10 w-auto object-contain"
              />
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>

            <nav
              className="flex-1 min-h-0 p-3 space-y-0.5 overflow-y-auto"
              data-testid="mobile-sidebar-nav"
            >
              <NavContent isCollapsed={false} onNavigate={handleNavigate} />
            </nav>

            <MobileFooter />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;